import api from './api';

// Data transformation utilities
const transformToCamelCase = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(transformToCamelCase);

    const camelCaseObj = {};
    for (const [key, value] of Object.entries(obj)) {
        const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        camelCaseObj[camelKey] = typeof value === 'object' ? transformToCamelCase(value) : value;
    }
    return camelCaseObj;
};

const transformToSnakeCase = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(transformToSnakeCase);

    const snakeCaseObj = {};
    for (const [key, value] of Object.entries(obj)) {
        const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        snakeCaseObj[snakeKey] = typeof value === 'object' ? transformToSnakeCase(value) : value;
    }
    return snakeCaseObj;
};

export const userService = {
    // Get user profile for profile-settings page
    getUserProfile: async () => {
        try {
            console.log('🔍 Fetching user profile for profile settings');
            const response = await api.get('/users/profile/');
            console.log('✅ Profile data received:', response.data);

            return {
                success: true,
                data: transformToCamelCase(response.data.data || response.data)
            };
        } catch (error) {
            console.error('❌ Error fetching user profile:', error);

            // Handle authentication errors
            if (error.response?.status === 401) {
                return {
                    success: false,
                    authError: true,
                    error: 'Authentication required'
                };
            }

            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.detail || error.message || 'Failed to fetch profile data'
            };
        }
    },

    // Update user profile from profile-settings page
    updateUserProfile: async (profileData) => {
        try {
            console.log('🔄 Updating user profile:', profileData);

            // Transform camelCase to snake_case for backend
            const transformedData = transformToSnakeCase(profileData);
            console.log('📤 Transformed data for backend:', transformedData);

            const response = await api.patch('/users/profile/', transformedData);
            console.log('✅ Profile updated successfully:', response.data);

            return {
                success: true,
                data: transformToCamelCase(response.data.data || response.data),
                message: response.data.message || 'Profile updated successfully'
            };
        } catch (error) {
            console.error('❌ Error updating profile:', error);
            console.error('📋 Error response:', error.response);

            // Handle authentication errors
            if (error.response?.status === 401) {
                return {
                    success: false,
                    authError: true,
                    error: 'Authentication required'
                };
            }

            // Handle validation errors
            if (error.response?.status === 400 && error.response?.data?.errors) {
                return {
                    success: false,
                    validationErrors: transformToCamelCase(error.response.data.errors),
                    error: 'Validation failed'
                };
            }

            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.detail || error.message || 'Failed to update profile'
            };
        }
    },

    // Get current user profile - CORRECT ENDPOINT
    getCurrentUser: async () => {
        try {
            console.log('🔍 Making API call to /users/profile/');
            const response = await api.get('/users/profile/');
            console.log('✅ API response received:', response);
            console.log('📄 Response data:', response.data);
            console.log('📊 Response status:', response.status);
            
            // Extract the actual user data from the response
            const userData = response.data.data || response.data;
            console.log('📄 Extracted user data:', userData);
            
            // Transform snake_case to camelCase for frontend consistency
            const transformedData = transformToCamelCase(userData);
            console.log('🔄 Transformed user data:', transformedData);
            
            return {
                success: true,
                data: transformedData
            };
        } catch (error) {
            console.error('❌ Error fetching current user:', error);
            console.error('📋 Error response:', error.response);

            // Handle email verification required
            if (error.response?.status === 403 &&
                error.response?.data?.error === 'email_verification_required') {
                return {
                    success: false,
                    requiresVerification: true,
                    email: error.response.data.email,
                    message: error.response.data.detail
                };
            }

            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch user data'
            };
        }
    },

    // Update current user profile - for AuthContext
    updateCurrentUser: async (userData) => {
        try {
            console.log('🔄 Updating current user:', userData);
            
            // Transform camelCase to snake_case for backend
            const transformedData = transformToSnakeCase(userData);
            console.log('📤 Transformed data for backend:', transformedData);
            
            const response = await api.patch('/users/profile/', transformedData);
            console.log('✅ User updated successfully:', response.data);
            
            // Transform response back to camelCase
            const transformedResponse = transformToCamelCase(response.data);
            
            return {
                success: true,
                data: transformedResponse,
                message: response.data.message || 'Profile updated successfully'
            };
        } catch (error) {
            console.error('❌ Error updating user:', error);
            
            if (error.response?.status === 401) {
                return {
                    success: false,
                    authError: true,
                    error: 'Authentication required'
                };
            }
            
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.detail || error.message || 'Failed to update user'
            };
        }
    },

    // Get user preferences
    getUserPreferences: async () => {
        try {
            const response = await api.get('/users/preferences/');
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('❌ Error fetching user preferences:', error);
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch preferences'
            };
        }
    },

    // Update user preferences
    updateUserPreferences: async (preferences) => {
        try {
            const response = await api.patch('/users/preferences/', preferences);
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('❌ Error updating preferences:', error);
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to update preferences'
            };
        }
    },

    // Upload profile picture
    uploadProfilePicture: async (file) => {
        try {
            const formData = new FormData();
            formData.append('profile_picture', file);

            const response = await api.patch('/users/profile/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('❌ Error uploading profile picture:', error);
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to upload profile picture'
            };
        }
    },

    // Delete user account
    deleteAccount: async () => {
        try {
            await api.delete('/users/profile/');
            return {
                success: true,
                message: 'Account deleted successfully'
            };
        } catch (error) {
            console.error('❌ Error deleting account:', error);
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to delete account'
            };
        }
    },

    // Get user statistics
    getUserStats: async () => {
        try {
            const response = await api.get('/users/stats/');
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            console.error('❌ Error fetching user stats:', error);
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch user statistics'
            };
        }
    },

    // Fetch available countries from django-countries
    getAvailableCountries: async () => {
        try {
            const response = await api.get('/core/locale/countries/');
            console.log('✅ Countries API response:', response.data);

            if (response.data.success) {
                return {
                    success: true,
                    data: response.data.data.countries
                };
            } else {
                console.error('❌ API returned error:', response.data);
                return {
                    success: false,
                    error: 'Failed to fetch countries'
                };
            }
        } catch (error) {
            console.error('❌ Countries API error:', error);
            return {
                success: false,
                error: 'Network error occurred'
            };
        }
    }
};

export default userService; 